import {
  AddressObj,
  PortfolioAvmThresholdValue,
  PortfolioConfigFields,
  PortfolioEventsFields,
  PortfolioEventsPayload,
  PortfolioEventsResponse,
  PortfolioFields,
  PortfolioForm,
  PortfolioFormFields,
  PortfolioFormNotificationTypes,
  PortfolioGtLtThreshold,
  PortfolioNotificationFields,
  PortfolioPayload,
  PortfolioResponse,
  PortfolioTableColumns,
  UploadStatus,
} from '@hcs/types';
import { formatTime } from '@hcs/utils';

import { NOTIFICATION_TYPE_OPTIONS_INIT } from '../constants/Portfolio.constants';
import { DEFAULT_PORTFOLIO_SETUP_FORM_VALUES } from '../constants/PortfolioForm.constants';

const NEW_PORTFOLIO_BASE_COLUMNS = [
  'closingDate',
  'closingPrice',
  'lastUpdate',
  'ltv',
] satisfies PortfolioTableColumns[];

const NEW_PORTFOLIO_COLUMNS_FOR_NOTIFICATION_TYPE = {
  [PortfolioFormNotificationTypes.Mls]: [
    'mlsStatus',
    'listingPrice',
    'listingDate',
  ],
  [PortfolioFormNotificationTypes.Ltv]: [
    'ltv',
    'loanContractDate',
    'interestRate',
  ],
  [PortfolioFormNotificationTypes.PropensityToList]: ['propensityToList'],
  [PortfolioFormNotificationTypes.PropensityToRefi]: ['propensityToRefi'],
  [PortfolioFormNotificationTypes.Avm]: ['avm'],
  [PortfolioFormNotificationTypes.Lien]: [],
  [PortfolioFormNotificationTypes.Nod]: [],
} satisfies Record<PortfolioFormNotificationTypes, PortfolioTableColumns[]>;

const transformGtLtThresholds = (
  thresholds: PortfolioForm[PortfolioFormFields.LtvThresholds],
  isPercentDecimal: boolean,
): PortfolioEventsPayload[PortfolioEventsFields.LtvThresholds] => {
  return (
    thresholds
      ?.filter((threshold): threshold is PortfolioGtLtThreshold => {
        return !!threshold.threshold;
      })
      .map((threshold) => ({
        ...threshold,
        threshold: isPercentDecimal
          ? threshold.threshold / 100
          : threshold.threshold,
      })) || null
  );
};

const transformAvmThresholds = (
  avmThresholds: PortfolioForm[PortfolioFormFields.AvmThresholds],
): PortfolioEventsPayload[PortfolioEventsFields.Avm] => {
  return avmThresholds && avmThresholds.thresholds
    ? {
        ...avmThresholds,
        thresholds: avmThresholds.thresholds
          .filter(
            (threshold): threshold is PortfolioAvmThresholdValue =>
              !!threshold.value,
          )
          .map((threshold) => ({
            ...threshold,
            value: threshold.value / 100,
          })),
      }
    : {};
};

export const getCreatePortfolioPayload = (
  formData: PortfolioForm,
): PortfolioPayload => {
  const {
    name,
    notificationType,
    mlsStatus,
    ltvThresholds,
    propensityToListThresholds,
    propensityToRefiThresholds,
    emails,
    avmThresholds,
    liens,
    webhookUrl,
    username,
    password,
  } = formData;
  const isLtvSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Ltv,
  );
  const isPropensityToListSelected = notificationType.includes(
    PortfolioFormNotificationTypes.PropensityToList,
  );
  const isPropensityToRefiSelected = notificationType.includes(
    PortfolioFormNotificationTypes.PropensityToRefi,
  );
  const isMlsSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Mls,
  );
  const isAvmSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Avm,
  );
  const isLienSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Lien,
  );
  const isNodSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Nod,
  );
  const webhookConfig =
    webhookUrl && username && password
      ? {
          [PortfolioNotificationFields.WebhookUrl]: webhookUrl,
          [PortfolioNotificationFields.WebhookUsername]: username,
          [PortfolioNotificationFields.WebhookPassword]: password,
        }
      : null;

  const transformedLtvThresholds = transformGtLtThresholds(ltvThresholds, true);
  const transformedPropensityToListThresholds = transformGtLtThresholds(
    propensityToListThresholds,
    false,
  );
  const transformedPropensityToRefiThresholds = transformGtLtThresholds(
    propensityToRefiThresholds,
    false,
  );
  const transformedAvmThresholds = transformAvmThresholds(avmThresholds);

  return {
    [PortfolioFields.Name]: name,
    [PortfolioFields.Config]: {
      [PortfolioConfigFields.Notification]: {
        [PortfolioNotificationFields.Mechanism]: 'EMAIL',
        [PortfolioNotificationFields.Emails]: emails,
      },
      [PortfolioConfigFields.Webhook]: webhookConfig,
      [PortfolioConfigFields.Events]: {
        [PortfolioEventsFields.Status]: isMlsSelected ? mlsStatus : null,
        [PortfolioEventsFields.Nod]: isNodSelected ? isNodSelected : null,
        [PortfolioEventsFields.LtvThresholds]: isLtvSelected
          ? transformedLtvThresholds
          : null,
        [PortfolioEventsFields.PropensityToList]: isPropensityToListSelected
          ? transformedPropensityToListThresholds
          : null,
        [PortfolioEventsFields.PropensityToRefi]: isPropensityToRefiSelected
          ? transformedPropensityToRefiThresholds
          : null,
        [PortfolioEventsFields.Avm]: isAvmSelected
          ? transformedAvmThresholds
          : null,
        [PortfolioEventsFields.Lien]: isLienSelected ? liens : null,
      },
    },
    // Compute a list of columns for the new portfolio based on the configured notification types
    [PortfolioFields.Columns]: [
      ...NEW_PORTFOLIO_BASE_COLUMNS,
      ...notificationType.reduce<PortfolioTableColumns[]>((acc, type) => {
        return [
          ...new Set([
            ...acc,
            ...NEW_PORTFOLIO_COLUMNS_FOR_NOTIFICATION_TYPE[type],
          ]),
        ];
      }, []),
    ],
  };
};

/**
 * @todo Create payload based on original portfolio
 *       - payload will contain only differences between
 *         form data and original state
 */
export const getUpdatePortfolioPayload = (
  formData: PortfolioForm,
  accessibleNotifications?: PortfolioFormNotificationTypes[],
): PortfolioPayload => {
  const {
    name,
    notificationType,
    mlsStatus,
    ltvThresholds,
    propensityToListThresholds,
    propensityToRefiThresholds,
    emails,
    avmThresholds,
    liens,
    webhookUrl,
    username,
    password,
  } = formData;

  const nodAccessible =
    accessibleNotifications &&
    accessibleNotifications?.includes(PortfolioFormNotificationTypes.Nod);
  const isPropensityToListAccessible = accessibleNotifications?.includes(
    PortfolioFormNotificationTypes.PropensityToList,
  );
  const isPropensityToRefiAccessible = accessibleNotifications?.includes(
    PortfolioFormNotificationTypes.PropensityToRefi,
  );

  const isLtvSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Ltv,
  );
  const isPropensityToListSelected = notificationType.includes(
    PortfolioFormNotificationTypes.PropensityToList,
  );
  const isPropensityToRefiSelected = notificationType.includes(
    PortfolioFormNotificationTypes.PropensityToRefi,
  );
  const isMlsSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Mls,
  );
  const isAvmSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Avm,
  );
  const isLienSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Lien,
  );
  const isNodSelected = notificationType.includes(
    PortfolioFormNotificationTypes.Nod,
  );

  const transformedLtvThresholds = transformGtLtThresholds(ltvThresholds, true);
  const transformedPropensityToListThresholds = transformGtLtThresholds(
    propensityToListThresholds,
    false,
  );
  const transformedPropensityToRefiThresholds = transformGtLtThresholds(
    propensityToRefiThresholds,
    false,
  );
  const transformedAvmThresholds = transformAvmThresholds(avmThresholds);

  const mlsPayload = isMlsSelected ? mlsStatus : [];
  const nodPayload = nodAccessible ? isNodSelected : null;
  const ltvPayload = isLtvSelected ? transformedLtvThresholds : [];
  const propensityToListPayload = isPropensityToListSelected
    ? transformedPropensityToListThresholds
    : isPropensityToListAccessible
      ? []
      : null;
  const propensityToRefiPayload = isPropensityToRefiSelected
    ? transformedPropensityToRefiThresholds
    : isPropensityToRefiAccessible
      ? []
      : null;

  const isAvmAccessible = accessibleNotifications?.includes(
    PortfolioFormNotificationTypes.Avm,
  );
  const avmPayload =
    isAvmSelected && isAvmAccessible
      ? transformedAvmThresholds
      : isAvmAccessible
        ? {}
        : null;

  const isLienAccessible = accessibleNotifications?.includes(
    PortfolioFormNotificationTypes.Lien,
  );
  const lienPayload =
    isLienSelected && isLienAccessible ? liens : isLienAccessible ? [] : null;

  const webhookConfig =
    webhookUrl && username && password
      ? {
          [PortfolioNotificationFields.WebhookUrl]: webhookUrl,
          [PortfolioNotificationFields.WebhookUsername]: username,
          [PortfolioNotificationFields.WebhookPassword]: password,
        }
      : webhookUrl
        ? {
            [PortfolioNotificationFields.WebhookUrl]: webhookUrl,
            [PortfolioNotificationFields.WebhookUsername]: null,
            [PortfolioNotificationFields.WebhookPassword]: null,
          }
        : null;

  return {
    [PortfolioFields.Name]: name,
    [PortfolioFields.Config]: {
      [PortfolioConfigFields.Notification]: {
        [PortfolioNotificationFields.Mechanism]: 'EMAIL',
        [PortfolioNotificationFields.Emails]: emails,
      },
      [PortfolioConfigFields.Events]: {
        [PortfolioEventsFields.Status]: mlsPayload,
        [PortfolioEventsFields.LtvThresholds]: ltvPayload,
        [PortfolioEventsFields.PropensityToList]: propensityToListPayload,
        [PortfolioEventsFields.PropensityToRefi]: propensityToRefiPayload,
        [PortfolioEventsFields.Avm]: avmPayload,
        [PortfolioEventsFields.Lien]: lienPayload,
        [PortfolioEventsFields.Nod]: nodPayload,
      },
      [PortfolioConfigFields.Webhook]: webhookConfig,
    },
  };
};

/**
 * 0.5 seconds per address is the agreed upon approx
 * process time by product and BE
 */
const APPROXIMATE_TIME_TO_PROCESS_ADDRESS = 0.5;
export const getEstimatedUploadCompletionTime = (
  uploadStatus: UploadStatus,
) => {
  const { totalAddressesInFile, processedCount } = uploadStatus;

  const addressesLeftToUpload = totalAddressesInFile - processedCount;
  const timeRemainingSeconds =
    APPROXIMATE_TIME_TO_PROCESS_ADDRESS * addressesLeftToUpload;

  const currentDate = new Date();
  currentDate.setSeconds(currentDate.getSeconds() + timeRemainingSeconds);

  return formatTime(currentDate);
};

const getNotificationsTypeArr = (
  mlsStatus: PortfolioEventsResponse[PortfolioEventsFields.Status],
  ltvThresholds: PortfolioEventsResponse[PortfolioEventsFields.LtvThresholds],
  propensityToListThresholds: PortfolioEventsResponse[PortfolioEventsFields.PropensityToList],
  propensityToRefiThresholds: PortfolioEventsResponse[PortfolioEventsFields.PropensityToRefi],
  avmThresholds: PortfolioEventsResponse[PortfolioEventsFields.Avm],
  liens: PortfolioEventsResponse[PortfolioEventsFields.Lien],
  nod: PortfolioEventsResponse[PortfolioEventsFields.Nod],
): PortfolioFormNotificationTypes[] => {
  const notificationsTypeArr = [];
  if (mlsStatus && mlsStatus.length > 0) {
    notificationsTypeArr.push(PortfolioFormNotificationTypes.Mls);
  }

  if (ltvThresholds && ltvThresholds.length > 0) {
    notificationsTypeArr.push(PortfolioFormNotificationTypes.Ltv);
  }

  if (propensityToListThresholds && propensityToListThresholds.length > 0) {
    notificationsTypeArr.push(PortfolioFormNotificationTypes.PropensityToList);
  }
  if (propensityToRefiThresholds && propensityToRefiThresholds.length > 0) {
    notificationsTypeArr.push(PortfolioFormNotificationTypes.PropensityToRefi);
  }

  if (avmThresholds)
    notificationsTypeArr.push(PortfolioFormNotificationTypes.Avm);

  if (liens && liens.length > 0)
    notificationsTypeArr.push(PortfolioFormNotificationTypes.Lien);

  if (nod) notificationsTypeArr.push(PortfolioFormNotificationTypes.Nod);

  return notificationsTypeArr;
};

export const getNotificationsTypeStr = (
  portfolio: PortfolioResponse,
): string => {
  const {
    config: {
      events: {
        status: mlsStatus,
        ltvThresholds,
        propensityToList,
        propensityToRefi,
        avm: avmThresholds,
        lien,
        nod,
      },
    },
  } = portfolio;

  const notificationTypeArr = getNotificationsTypeArr(
    mlsStatus,
    ltvThresholds,
    propensityToList,
    propensityToRefi,
    avmThresholds,
    lien,
    nod,
  );
  return notificationTypeArr
    .map(
      (type) =>
        NOTIFICATION_TYPE_OPTIONS_INIT.find(
          (typeOption) => typeOption.value === type,
        )?.label,
    )
    .join(', ');
};

export const getFormattedInitialPortfolioFormData = (
  data: PortfolioResponse,
): PortfolioForm => {
  const {
    name,
    config: {
      events: {
        status: mlsStatus,
        ltvThresholds,
        propensityToList,
        propensityToRefi,
        avm: avmThresholds,
        lien,
        nod,
      },
      notification,
      webhook,
    },
  } = data;

  const notificationTypeArr = getNotificationsTypeArr(
    mlsStatus,
    ltvThresholds,
    propensityToList,
    propensityToRefi,
    avmThresholds,
    lien,
    nod,
  );
  const initialMlsStatus =
    mlsStatus && mlsStatus.length > 0
      ? mlsStatus
      : DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[PortfolioFormFields.MlsStatus];

  const initialLiens =
    lien && lien.length > 0
      ? lien
      : DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[PortfolioFormFields.Liens];

  const initialNod = nod
    ? nod
    : DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[PortfolioFormFields.Nod];

  const transformedLtvThresholds =
    ltvThresholds?.map((threshold) => ({
      ...threshold,
      threshold: threshold.threshold * 100,
    })) ||
    DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[PortfolioFormFields.LtvThresholds];
  const transformedPropensityToList =
    propensityToList ||
    DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[
      PortfolioFormFields.PropensityToListThresholds
    ];
  const transformedPropensityToRefi =
    propensityToRefi ||
    DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[
      PortfolioFormFields.PropensityToRefiThresholds
    ];

  const transformedAvmThresholds: PortfolioForm[PortfolioFormFields.AvmThresholds] =
    avmThresholds
      ? {
          ...avmThresholds,
          thresholds:
            avmThresholds?.thresholds.map((threshold) => ({
              ...threshold,
              // Convert percent to display value and limit to 2 decimal places i.e. 0.1234 => 12.34
              value: parseFloat((threshold.value * 100).toFixed(2)),
            })) || null,
        }
      : DEFAULT_PORTFOLIO_SETUP_FORM_VALUES[PortfolioFormFields.AvmThresholds];

  return {
    [PortfolioFormFields.Name]: name,
    [PortfolioFormFields.NotificationType]: notificationTypeArr,
    [PortfolioFormFields.MlsStatus]: initialMlsStatus,
    [PortfolioFormFields.LtvThresholds]: transformedLtvThresholds,
    [PortfolioFormFields.PropensityToListThresholds]:
      transformedPropensityToList,
    [PortfolioFormFields.PropensityToRefiThresholds]:
      transformedPropensityToRefi,
    [PortfolioFormFields.AvmThresholds]: transformedAvmThresholds,
    [PortfolioFormFields.Liens]: initialLiens,
    [PortfolioFormFields.Nod]: initialNod,
    [PortfolioFormFields.Emails]: notification?.emails || [],
    [PortfolioFormFields.WebhookUrl]: webhook?.webhookUrl || null,
  };
};

export const getAssetFullAddress = ({
  address,
  unit,
  city,
  state,
  zipcode,
}: AddressObj) => {
  return `${address}
  ${unit && ` ${unit}`}
  ${city && `, ${city}`}
  ${state && `, ${state}`}
  ${zipcode && ` ${zipcode}`}`;
};
